/**
 * 앱 무결성 증명 — 네이티브 플러그인 소비 접합부.
 *
 * PLAN_ASSET_DELIVERY_HARDENING.md Phase 4. BE `domains/attest` 와 짝이다.
 *
 * 계약은 **한 함수**다 — `getAttestation(challenge)`. Android(Play Integrity)와
 * iOS(App Attest)의 차이는 네이티브가 숨긴다(D11: 나중에 플러그인으로 떼어낼 때 그대로
 * 잘려 나가게 하려는 것).
 *
 * ⚠ **실패가 앱을 막지 않는다.** 증명을 못 만드는 정상적인 상황이 많다 —
 * 시뮬레이터·에뮬레이터, Play services 없는 기기, dev 플레이버(별도 패키지라 Play 가 모른다),
 * 웹. 그래서 실패는 null 이고, **판정은 BE 의 모드가 정한다**(OFF/SHADOW/ENFORCE).
 * 여기서 막으면 SHADOW 의 의미가 사라진다 — 관측하려고 만든 모드다.
 */
import { registerPlugin } from "@capacitor/core";
import { getPlatform, isNative, nativeWarn } from "./index";

interface AppAttestPlugin {
  getAttestation(options: { challenge: string }): Promise<{ attestation: string; keyId?: string }>;
}

const AppAttest = registerPlugin<AppAttestPlugin>("AppAttest");

export interface Attestation {
  /** BE `POST /assets/token` 의 platform 필드 */
  platform: "ANDROID" | "IOS";
  /** 플랫폼 증명 원문 — Play Integrity 토큰 또는 App Attest attestation(base64) */
  attestation: string;
}

/**
 * challenge 에 묶인 증명을 만든다. 못 만들면 null (호출부는 증명 없이 진행한다).
 *
 * ⚠ challenge 는 **BE 가 방금 발급한 값**이어야 한다. 재사용하면 BE 가
 * `CHALLENGE_INVALID` 로 거른다(1회성 소비) — 그게 재생 차단의 본체다.
 */
export async function getAttestation(challenge: string): Promise<Attestation | null> {
  if (!isNative()) return null;
  const platform = getPlatform() === "ios" ? "IOS" : "ANDROID";
  try {
    const res = await AppAttest.getAttestation({ challenge });
    if (!res?.attestation) return null;
    return { platform, attestation: res.attestation };
  } catch (err) {
    // 정상적인 실패가 많은 경로다 — 경고로만 남기고 흐름을 막지 않는다.
    nativeWarn("appAttest:getAttestation", err);
    return null;
  }
}
